import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';

const API = appConfig.apis.signatureApiEnv;
const baseUrl = API.address;

export class ApiError extends Error {
    constructor(public status: number, message: string) {
        super(message);
    }
}

const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

const handleResponse = async (res: Response) => {
    if (res.ok) {
        if (res.status === 204 || res.headers.get('content-length') === '0') return {};
        const text = await res.text();
        return text ? JSON.parse(text) : {};
    }
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.message || `API error: ${res.status}`);
};

const get = async (path: string) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { headers: await getHeaders() }));

const post = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'POST', headers: await getHeaders(), body: JSON.stringify(data) }));

const put = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'PUT', headers: await getHeaders(), body: JSON.stringify(data) }));

const del = async (path: string) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'DELETE', headers: await getHeaders() }));

const qs = (params: Record<string, unknown>) => {
    const s = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') s.set(k, String(v)); });
    const str = s.toString();
    return str ? `?${str}` : '';
};

export const signatureApi = {
    // SignatureProcess
    getProcessList: (params?: { page?: number; size?: number; status?: string; search?: string }) =>
        get(`/signatures/page-list${qs(params || {})}`),

    getProcessDetail: (id: string) => get(`/signatures/${id}`),

    createProcess: (data: { documentUuid: string; operationNumber: string; providerId: string; observers?: { personUuid: string; name: string; email: string }[] }) =>
        post('/signatures', data),

    sendProcess: (id: string) => post(`/signatures/${id}/send`, {}),

    retryProcess: (id: string, retriedBy: string) => post(`/signatures/${id}/retry`, { retriedBy }),

    cancelProcess: (id: string, cancelReason: string, cancelledBy: string) =>
        post(`/signatures/${id}/cancel`, { cancelReason, cancelledBy }),

    completeProcess: (id: string, completeType: string, completedBy?: string, completeReason?: string) =>
        post(`/signatures/${id}/complete`, { completeType, completedBy, completeReason }),

    freezeProcess: (id: string, reason: string, frozenBy: string) =>
        post(`/signatures/${id}/freeze`, { reason, frozenBy }),

    unfreezeProcess: (id: string) => post(`/signatures/${id}/unfreeze`, {}),

    retrieveProcess: (id: string, retrieveReason: string, retrievedBy: string) =>
        post(`/signatures/${id}/retrieve`, { retrieveReason, retrievedBy }),

    // Documents
    uploadDocument: (fileName: string, fileContent: string, operationNumber: string, uploadedBy: string) =>
        post('/documents/upload', { fileName, fileContent, operationNumber, uploadedBy }),

    extractDocument: (documentId: string, s3Key: string) =>
        post(`/documents/${documentId}/extract`, { s3Key }),

    sendFeedback: (documentId: string, extractionId: string, feedbacks: { field: string; extractedValue: string }[]) =>
        post(`/documents/${documentId}/extract/feedback`, { extractionId, feedbacks }),

    retryExtraction: (documentId: string) =>
        post(`/documents/${documentId}/extract/retry`, {}),

    confirmExtraction: (documentId: string, editedFields?: Record<string, string>, confirmedBy?: string) =>
        post(`/documents/${documentId}/extract/confirm`, { editedFields, confirmedBy }),

    // Providers
    getProviders: () => get('/providers'),

    // Document Types
    getDocumentTypes: () => get('/document-types'),
};
